import React, { useState, useEffect, useRef } from 'react';
import { Phone, ArrowRight, Shield, ChevronDown } from 'lucide-react';
import { companyInfo, mottos } from '../data/mock';
import { Button } from './ui/button';

const HeroSection = () => {
  const [currentMotto, setCurrentMotto] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  const [typewriterText, setTypewriterText] = useState('');
  const [typewriterComplete, setTypewriterComplete] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);

  const fullTitle = "Adevărul se ascunde în detalii. Detectivii Cabinetului din Bacau îl vor găsi!";

  // Parallax effect
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Flashlight/lantern effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        setMousePos({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top
        });
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Typewriter effect
  useEffect(() => {
    let index = 0;
    const timer = setInterval(() => {
      if (index <= fullTitle.length) {
        setTypewriterText(fullTitle.slice(0, index));
        index++;
      } else {
        setTypewriterComplete(true);
        clearInterval(timer);
      }
    }, 80);
    return () => clearInterval(timer);
  }, []);

  // Rotate mottos every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setFadeIn(false);
      setTimeout(() => {
        setCurrentMotto((prev) => (prev + 1) % mottos.length);
        setFadeIn(true);
      }, 300);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const scrollToContact = (e) => {
    e.preventDefault();
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToServices = (e) => {
    e.preventDefault();
    document.querySelector('#servicii')?.scrollIntoView({ behavior: 'smooth' });
  };

  const bannerImage = "https://customer-assets.emergentagent.com/job_sleuth-focsani-clone/artifacts/sysfqo01_Detectivi%20Bacau.jpg";

  return (
    <section
      id="acasa"
      ref={heroRef}
      className="relative min-h-[90vh] flex items-center justify-center overflow-hidden"
      aria-labelledby="hero-heading"
      itemScope
      itemType="https://schema.org/WPHeader"
    >
      {/* Parallax Background */}
      <div 
        className="absolute inset-0"
        style={{ transform: `translateY(${scrollY * 0.5}px)` }}
      >
        <img
          src={bannerImage}
          alt="Detectiv Bacau - Detectiv particular profesionist efectuând supraveghere discretă pentru servicii de investigații private licențiate IGPR în județul Bacău"
          className="w-full h-[120%] object-cover"
          loading="eager"
          fetchpriority="high"
        />
      </div>

      {/* Dark overlay with gradient - minimal for maximum image visibility */}
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900/60 via-slate-900/30 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent" />

      {/* Flashlight/Lantern Effect */}
      <div 
        className="absolute inset-0 pointer-events-none transition-opacity duration-100"
        style={{
          background: `radial-gradient(circle 300px at ${mousePos.x}px ${mousePos.y}px, transparent 0%, rgba(15, 23, 42, 0.4) 100%)`
        }}
      />

      {/* Content */}
      <div 
        className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-10"
        style={{ transform: `translateY(${scrollY * 0.1}px)` }}
      >
        <div className="max-w-2xl">
          {/* Badge with fade-in */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-8 animate-fade-in-down">
            <Shield className="w-4 h-4 text-white" />
            <span className="text-white/90 text-sm">Agenție Licențiată IGPR</span>
          </div>

          {/* Rotating Motto */}
          <div className="h-16 mb-6">
            <p 
              className={`text-xl md:text-2xl text-white/80 italic transition-all duration-300 ${
                fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
              }`}
            >
              "{mottos[currentMotto]}"
            </p>
          </div>

          {/* Typewriter Headline */}
          <h1 
            id="hero-heading"
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight min-h-[1.4em]"
            itemProp="headline"
          >
            <span className="inline">
              {typewriterText}
              <span className={`inline-block w-[4px] h-[0.9em] bg-amber-400 ml-1 align-middle ${typewriterComplete ? 'animate-blink' : 'animate-pulse'}`} aria-hidden="true" />
            </span>
          </h1>

          {/* Main promotional text */}
          <div className="mb-8 animate-fade-in-up animation-delay-400">
            <p className="text-xl sm:text-2xl md:text-3xl text-amber-400 font-semibold mb-4 leading-relaxed">
              Nu lăsa vremea să șteargă adevărul!
            </p>
            <p className="text-lg sm:text-xl md:text-2xl text-white mb-6 leading-relaxed max-w-3xl">
              Sună acum Detectivii din Bacău pentru o consultație gratuită și discreție garantată!
            </p>
          </div>

          {/* Subheadline with slide-in */}
          <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-10 leading-relaxed max-w-3xl animate-fade-in-up animation-delay-500">
            Fie că vrei doar să știi ce se petrece, fie că te pregătești pentru un proces, 
            Detectivii din Bacau sunt alături de tine cu profesionalism, discreție și dovezi care contează.
          </p>

          {/* CTA Buttons with Glow/Pulse Effect */}
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-700">
            <Button
              onClick={scrollToContact}
              size="lg"
              className="bg-white text-slate-900 hover:bg-slate-100 font-bold px-8 sm:px-10 py-6 sm:py-7 text-base sm:text-lg relative overflow-hidden group cta-glow"
            >
              <span className="relative z-10 flex items-center">
                Consultație Gratuită
                <ArrowRight className="w-5 sm:w-6 h-5 sm:h-6 ml-2 group-hover:translate-x-1 transition-transform" />
              </span>
              <span className="absolute inset-0 bg-gradient-to-r from-white via-slate-100 to-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </Button>
            <a href={`tel:${companyInfo.phone}`}>
              <Button
                variant="outline"
                size="lg"
                className="border-white/30 text-white hover:bg-white/10 px-8 sm:px-10 py-6 sm:py-7 text-base sm:text-lg w-full sm:w-auto cta-pulse"
              >
                <Phone className="w-5 sm:w-6 h-5 sm:h-6 mr-2" />
                Sună Acum
              </Button>
            </a>
          </div>

          {/* Trust indicators */}
          <div className="mt-12 flex items-center gap-8 text-sm text-slate-400 animate-fade-in-up animation-delay-900">
            <span>✓ Confidențialitate 100%</span>
            <span>✓ Dovezi Legale</span>
            <span className="hidden sm:inline">✓ Program 24/7</span>
          </div>
        </div>
      </div>

      {/* Scroll indicator with bounce */}
      <button 
        onClick={scrollToServices}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 hover:text-white transition-colors"
      >
        <ChevronDown className="w-8 h-8 animate-bounce" />
      </button>
    </section>
  );
};

export default HeroSection;
